import { useState, type FC } from 'react';
import BaseLoader from 'components/atoms/base-loader';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { useGetPatientSnapshot } from 'infrastructure/hooks/patient/use-get-snapshot';
import { useTranslation } from 'react-i18next';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { DateFormatter } from 'infrastructure/functions';
import dayjs from 'dayjs';
import i18n from 'infrastructure/i18n';
import { PresetKeyEnum } from 'infrastructure/enums';
import TwoWaySyncIcon from 'components/atoms/icons/two-way-sync';
import InformationTooltip from 'components/atoms/information-tooltip';
import BaseButton from 'components/atoms/baseButton';
import TrendUpIcon from 'components/atoms/icons/trend-up-icon';
import TrendDownIcon from 'components/atoms/icons/trend-down-icon';
import InformationIcon from 'components/atoms/icons/informationIcon';
import IIcon from 'components/atoms/icons/i-icon';
import { useGetPatientSnapshotSummary } from 'infrastructure/hooks/patient/use-get-snapshot-summary';
import { endOfDay, startOfDay } from 'date-fns';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import SearchIcon from 'components/atoms/icons/search.svg?react';
import EligibilityIcon from 'components/atoms/icons/eligibility';

import SummaryModal from './summary-modal';
import ReadingCard from './reading-card';
import s from './styles.module.scss';
import SummariesDropdown, {
  SUMMARY_BUTTONS,
} from './summary-modal/dropdown-btn';
import StatusTag from './status-tag';

import type { TSnapshotSummaryType } from 'infrastructure/interfaces';
import type { IValue } from 'components/atoms/base-range-datepicker';

interface PatientSnapshotProps {
  patientGuid: string;
}

// used one day less because we select a full day from 00:00 as the start and 23:59 as the end
const presets = [
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 7,
        })}
      </span>
    ),
    value: [
      dayjs().add(-6, 'd').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.LastSevenDays,
    index: 7,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 30,
        })}
      </span>
    ),
    value: [
      dayjs().add(-29, 'd').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.LastThirtyDays,
    index: 30,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountMonths', {
          count: 3,
        })}
      </span>
    ),
    value: [
      dayjs().add(-2, 'M').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.ThreeMonthsPast,
  },
];

const PatientSnapshot: FC<PatientSnapshotProps> = ({ patientGuid }) => {
  const [openModalType, setOpenModalType] =
    useState<TSnapshotSummaryType | null>(null);
  const [dates, setDates] = useState<IValue | null>(presets[1].value);
  const { patientInfo, loading } = usePatientInfo({ patientGuid });
  const { patientSnapshot, isLoading } = useGetPatientSnapshot({
    patientGuid,
    dates,
  });
  const { patientSnapshotSummary, isLoading: isLoadingSummary } =
    useGetPatientSnapshotSummary({
      patientGuid,
      type: openModalType,
      dates,
    });
  const { t } = useTranslation();
  const twoWaySync = patientInfo?.interactionStatus;
  const {
    lastContact,
    clinicEscalationCount,
    lastDeviceDeliveryDate,
    isEligible,
  } = patientSnapshot ?? {};

  const lastContactType =
    lastContact?.type === 'sms'
      ? lastContact.type.toUpperCase()
      : lastContact?.type;

  const escalationCount = clinicEscalationCount ?? 0;
  const escalationTimes = escalationCount > 1 ? 'times' : 'time';

  const loader = (
    <div className={s['loading-box']}>
      <BaseLoader loading />
    </div>
  );

  const tooltipContent = (
    <div className={s.tooltip}>
      <p data-title>
        <InformationIcon />
        {t('patientSnapshot.indicators')}
      </p>
      <p>
        <TrendUpIcon />
        {t('patientSnapshot.trendUpText')}
      </p>
      <p>
        <TrendDownIcon />
        {t('patientSnapshot.trendDownText')}
      </p>
      <p>
        {t('patientSnapshot.noIndicator')}
        {': '}
        {t('patientSnapshot.noIndicatorText')}
      </p>
    </div>
  );

  const content = (
    <>
      <div className={s.header}>
        <div className={s['header-left']}>
          <div className={s['title-wrapper']}>
            <h3 className={s.title}>{t('patientSnapshot.patientSnapshot')}</h3>
            <div className={s['last-data-wrapper']}>
              <p className={s['last-contact']}>
                {`${t('patientSnapshot.lastContact')}: `}
                <span>
                  {patientSnapshot?.lastContact ? (
                    <>
                      {lastContactType}{' '}
                      {DateFormatter({ date: lastContact?.date })}
                    </>
                  ) : (
                    t('labels.noData')
                  )}
                </span>
              </p>
              <p className={s['last-contact']}>
                {`${t('patientSnapshot.lastShipmentDate')}: `}
                <span>
                  {lastDeviceDeliveryDate ? (
                    <>{DateFormatter({ date: lastDeviceDeliveryDate })}</>
                  ) : (
                    t('labels.noData')
                  )}
                </span>
              </p>
            </div>
            <p className={s['escalation-count']}>
              {`${t('patientSnapshot.clinicEscalation')}: `}
              <span>
                {clinicEscalationCount ?? 0} {escalationTimes}
              </span>
            </p>
          </div>
          <div className={s.summary}>
            <SummariesDropdown setOpenModalIdx={setOpenModalType} />
          </div>
        </div>
        <div className={s['header-right']}>
          <div className={s['date-wrapper']}>
            <p className={s['date-label']}>{t('labels.dateRange')}</p>
            <div className={s.date}>
              <BaseRangeDatepicker
                withPresets
                value={dates || undefined}
                onChange={(range) => {
                  if (!range) return;
                  const [start, end] = range;
                  setDates([startOfDay(start), endOfDay(end)]);
                }}
                presets={() => presets}
                allowClear={false}
              />
            </div>
          </div>
          <div className={s['header-right-block']}>
            <div className={s['patient-info-wrapper']}>
              <StatusTag
                icon={<EligibilityIcon />}
                value={isEligible}
                label={t('patientSnapshot.eligibility')}
              />
              <StatusTag
                icon={<TwoWaySyncIcon />}
                value={twoWaySync}
                label={t('labels.twoWaySync')}
              />
            </div>
            <InformationTooltip
              icon={
                <BaseButton
                  type="secondary"
                  className={s['tooltip-btn']}
                  icon={<IIcon width={20} height={20} />}
                />
              }
              contentWrapperClassName={s['tooltip-content-wrapper']}
              content={tooltipContent}
            />
          </div>
        </div>
      </div>
      {patientSnapshot?.data?.length === 0 ? (
        <div className={s['empty-data-wrapper']}>
          <EmptyTablePlaceholder
            text={t('patientSnapshot.noDataFound')}
            icon={<SearchIcon width={70} height={70} />}
            column
          />
        </div>
      ) : (
        <div className={s.body}>
          <div className={s['readings-wrapper']}>
            {patientSnapshot?.data?.map((reading, index) => {
              return (
                <ReadingCard
                  key={index}
                  type={reading.type}
                  values={reading.data}
                  statusPercentages={reading.data.statusPercentages}
                  trend={reading.data.trend}
                />
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <div id="patient-snapshot" className={s.wrapper}>
        {loading || isLoading ? loader : content}
      </div>
      {openModalType && (
        <SummaryModal
          open={!!openModalType}
          onCancel={() => setOpenModalType(null)}
          subTitle={SUMMARY_BUTTONS[openModalType]}
          summary={patientSnapshotSummary?.summary}
          loading={isLoadingSummary}
        />
      )}
    </>
  );
};
export default PatientSnapshot;
